<template>
  <div class="v2-retailer-single-cart">
    <!-- ─── Top action bar ─────────────────────────────────────────────── -->
    <div
      v-if="showTotal"
      class="v2-retailer-single-cart__actions"
    >
      <div class="v2-retailer-single-cart__actions-left">
        <router-link
          v-slot="{ navigate }"
          :to="{
            name: 'v2-collection/products',
            params: { collectionId: collectionId },
          }"
          custom
        >
          <v2-button
            variant="outline"
            size="md"
            title="Back to Collection"
            @click="gotoCollection(navigate, $event)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </v2-button>
        </router-link>
        <v2-button
          variant="secondary"
          size="md"
          @click="showBuyingSummary"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="3"
              y1="12"
              x2="21"
              y2="12"
            />
            <line
              x1="3"
              y1="6"
              x2="21"
              y2="6"
            />
            <line
              x1="3"
              y1="18"
              x2="21"
              y2="18"
            />
          </svg>
          <span>Buying Summary</span>
        </v2-button>
      </div>
      <div class="v2-retailer-single-cart__actions-right">
        <v2-button
          variant="outline"
          size="md"
          @click="openImportModal"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line
              x1="12"
              y1="15"
              x2="12"
              y2="3"
            />
          </svg>
          <span>Import</span>
        </v2-button>
        <v2-button
          variant="outline"
          size="md"
          @click="onClickExportCart"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line
              x1="12"
              y1="3"
              x2="12"
              y2="15"
            />
          </svg>
          <span>Export</span>
        </v2-button>
      </div>
    </div>

    <!-- ─── Two-column layout: Notes+Address | Order Details ────────── -->
    <div
      v-if="showTotal"
      class="v2-retailer-single-cart__grid"
    >
      <!-- LEFT COLUMN: Notes, Delivery Window, Shipping Address -->
      <v2-card class="v2-retailer-single-cart__left">
        <!-- Notes -->
        <div class="v2-retailer-single-cart__section-header">
          <h4 class="v2-retailer-single-cart__section-title">
            Notes
          </h4>
          <v2-button
            v-if="isEditNotes && isRetailer"
            variant="primary"
            size="sm"
            :disabled="isSavingNotes"
            @click="onUpdateNotes"
          >
            {{ isSavingNotes ? 'Saving...' : 'Save' }}
          </v2-button>
          <button
            v-if="!isEditNotes"
            class="v2-retailer-single-cart__edit-btn"
            @click="handleNotesEdit"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
        <textarea
          v-if="isEditNotes"
          v-model="notes"
          class="v2-retailer-single-cart__notes-input"
          placeholder="Enter Notes"
        />
        <span
          v-else
          class="v2-retailer-single-cart__notes-text"
        >{{ notes }}</span>

        <!-- Delivery Window -->
        <div class="v2-retailer-single-cart__field">
          <span>Requested Delivery Window:</span>
          <span v-if="!isEditDeliveryWindow">
            {{ expectedDeliveryDate || '—' }}
            <button
              v-if="isRetailer"
              class="v2-retailer-single-cart__edit-btn"
              style="display: inline-flex; vertical-align: middle"
              @click="handleDeliveryWindowEdit"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
          <div
            v-if="isEditDeliveryWindow"
            class="v2-retailer-single-cart__delivery-edit"
          >
            <flat-pickr
              ref="deliveryWindowDate"
              v-model="expectedDeliveryDate"
              class="form-control"
              style="max-width: 140px; font-size: 0.8125rem"
              :config="VUE_FLATPICKR_MONTH_CONFIG"
              :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
            />
            <v2-button
              variant="primary"
              size="sm"
              :disabled="isSavingDeliveryWindow"
              @click="onUpdateDeliveryWindow"
            >
              {{ isSavingDeliveryWindow ? 'Saving...' : 'Save' }}
            </v2-button>
          </div>
        </div>

        <!-- Shipping Address -->
        <div
          v-if="profileData"
          class="v2-retailer-single-cart__address-section"
        >
          <div class="v2-retailer-single-cart__address-heading">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="1"
                y="3"
                width="15"
                height="13"
              />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle
                cx="5.5"
                cy="18.5"
                r="2.5"
              />
              <circle
                cx="18.5"
                cy="18.5"
                r="2.5"
              />
            </svg>
            <h4 class="v2-retailer-single-cart__section-title">
              Shipping Address
            </h4>
          </div>

          <!-- Existing shipping address -->
          <div v-if="displayShippingAddress">
            <v2-address-detail-ops :address="displayShippingAddress" />
            <v2-button
              variant="secondary"
              style="width: 100%; margin-top: 0.75rem"
              @click="getUserProfile(true)"
            >
              Select Another Address
            </v2-button>
          </div>

          <!-- No shipping address -->
          <div v-else-if="profileData">
            <p class="v2-retailer-single-cart__address-note">
              Please add a Shipping Address.
            </p>
            <v2-button
              variant="outline"
              style="width: 100%"
              @click="handleClickAddressBtn('shipping')"
            >
              Add Shipping Address
            </v2-button>
          </div>

        </div>

        <!-- Billing Address (only when missing — prompt user to add) -->
        <div
          v-if="profileData && !displayBillingAddress"
          class="v2-retailer-single-cart__address-section"
          style="margin-top: 0.5rem"
        >
          <v2-button
            variant="outline"
            style="width: 100%"
            @click="handleClickAddressBtn('billing')"
          >
            Add Billing Address
          </v2-button>
        </div>
      </v2-card>

      <!-- RIGHT COLUMN: Order Details -->
      <v2-card class="v2-retailer-single-cart__right">
        <div class="v2-retailer-single-cart__section-header">
          <h4 class="v2-retailer-single-cart__section-title">
            Order Details{{ currency ? ` (${currency})` : '' }}
          </h4>
        </div>

        <div class="v2-retailer-single-cart__totals">
          <div class="v2-retailer-single-cart__total-row">
            <span>Total Value</span>
            <span>{{ formatCurrency(totalMRP, currency) }}</span>
          </div>
          <div class="v2-retailer-single-cart__total-row">
            <span>Total Units</span>
            <span>{{ formatNumber(totalUnits) }}</span>
          </div>

          <!-- Credits -->
          <div
            v-if="discountInCredit > 0"
            class="v2-retailer-single-cart__credits"
          >
            <div class="v2-retailer-single-cart__credits-label">
              Available Credit Points
              <span
                v-if="!showCreditInput"
                class="v2-retailer-single-cart__credits-action"
                @click="showCreditInput = true"
              >
                {{ creditsUsed > 0 ? 'Edit' : 'Use credit points' }}
              </span>
            </div>
            <div class="v2-retailer-single-cart__total-row">
              <span>{{ remainingCredits }} credits available</span>
              <span v-if="!showCreditInput && appliedCredits > 0">
                -{{ formatCurrency(appliedCredits, currency) }}
              </span>
            </div>
            <div
              v-if="showCreditInput"
              class="v2-retailer-single-cart__credits-input-row"
            >
              <input
                v-model.number="creditsUsed"
                type="number"
                class="v2-retailer-single-cart__credits-input"
                :max="getMaxCreditEligible"
                min="0"
              >
              <v2-button
                variant="primary"
                size="sm"
                @click="applyCredits"
              >
                Apply
              </v2-button>
            </div>
          </div>

          <div class="v2-retailer-single-cart__divider" />

          <div class="v2-retailer-single-cart__total-row v2-retailer-single-cart__total-row--bold">
            <span>Total</span>
            <span>{{ formatCurrency(total, currency) }}</span>
          </div>
        </div>

        <!-- Terms & Conditions -->
        <div class="v2-retailer-single-cart__terms">
          <div class="v2-retailer-single-cart__terms-row">
            <label class="v2-retailer-single-cart__terms-checkbox">
              <input
                v-model="acceptTerms"
                type="checkbox"
                @change="trackTermsAndConditionsCheck($event.target.checked)"
              >
              I agree to the
            </label>
            <span
              class="v2-retailer-single-cart__terms-link"
              @click="handleTermsAndConditionLink(true)"
            >
              terms and conditions
            </span>
          </div>
          <div
            v-if="isTermsUnChecked && !acceptTerms"
            class="v2-retailer-single-cart__terms-error"
          >
            Please accept the Terms and Conditions
          </div>
        </div>

        <!-- Place Order -->
        <div class="v2-retailer-single-cart__place-order">
          <v2-button
            variant="primary"
            :loading="isPlacingOrder"
            :disabled="isDisabledPlaceOrder"
            style="width: 100%"
            @click="handlePlaceOrder"
          >
            Place Order
          </v2-button>
        </div>
      </v2-card>
    </div>

    <!-- ─── Cart Products ──────────────────────────────────────────────── -->
    <v2-cart-products
      ref="cartProducts"
      :cart-id="cartId"
      @update-total="updateCartTotal"
      @update-total-units="updateCartTotalUnits"
      @has-cart-product="updateListView"
      @update-notes="(note) => { notes = note }"
      @clear-cart="$refs.cartModals.show('clear-cart')"
    />

    <!-- ─── Buying Summary Modal ───────────────────────────────────────── -->
    <v2-buying-summary-modal
      :show="showBuyingSummaryModal"
      :cart-list="buyingSummaryCartList"
      :cart-values-list="buyingSummaryCartValuesList"
      :total-order-value="buyingSummaryTotalOrderValue"
      :is-multiple-cart="false"
      @close="closeBuyingSummary"
    />

    <!-- ─── Terms Modal ────────────────────────────────────────────────── -->
    <v2-modal
      :show="showTermsModal"
      title="Terms and Conditions"
      size="xl"
      @close="showTermsModal = false; handleTermsModalClose()"
    >
      <div class="v2-cart-terms">
        <div class="v2-cart-terms__heading">
          GENERAL SALE CONDITIONS
        </div>
        <div
          v-for="section in cartTermsContent"
          :key="section.title"
          class="v2-cart-terms__section"
        >
          <div class="v2-cart-terms__section-title">
            {{ section.title }}
          </div>
          <p
            v-for="point in section.points"
            :key="point"
            class="v2-cart-terms__point"
          >
            {{ point }}
          </p>
        </div>
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showTermsModal = false; handleTermsModalClose()"
        >
          Close
        </v2-button>
        <v2-button
          variant="primary"
          @click="updateTerms(); showTermsModal = false"
        >
          Accept
        </v2-button>
      </template>
    </v2-modal>

    <!-- ─── Order Fail Modal ───────────────────────────────────────────── -->
    <v2-modal
      :show="showOrderFailModal"
      title="Order Creation Failed"
      size="md"
      @close="showOrderFailModal = false"
    >
      <order-validation-message
        v-if="profileData"
        :profile-data="profileData"
      />
      <template #footer>
        <v2-button
          variant="primary"
          @click="$router.push({ name: 'profile' })"
        >
          Edit Profile
        </v2-button>
      </template>
    </v2-modal>

    <!-- ─── Address Select Modal (shipping) ────────────────────────────── -->
    <v2-modal
      :show="showSelectAddressModal"
      title="Select Shipping Address"
      size="lg"
      @close="showSelectAddressModal = false"
    >
      <div
        v-for="addr in addressComposable.shippingAddress.value.filter(a => a._id)"
        :key="addr._id"
        class="v2-retailer-single-cart__address-select-item"
        @click="onSelectShippingAddress(addr)"
      >
        <input
          type="radio"
          :checked="addr.isPrimary"
          :value="addr._id"
          name="shipping-address-select"
        >
        <div class="v2-retailer-single-cart__address-select-body">
          <v2-address-detail-ops :address="addr" />
        </div>
        <v2-button
          variant="outline"
          size="sm"
          class="v2-retailer-single-cart__address-edit-btn"
          @click.stop="editShippingAddress(addr)"
        >
          Edit
        </v2-button>
      </div>
      <div class="v2-retailer-single-cart__address-add-row">
        <v2-button
          variant="outline"
          style="width: 100%"
          @click="addNewShippingAddress"
        >
          + Add New Shipping Address
        </v2-button>
      </div>
    </v2-modal>

    <!-- ─── Add Billing Address Modal ──────────────────────────────────── -->
    <v2-modal
      :show="showAddBillingModal"
      :title="billingFormAddress && billingFormAddress._id ? 'Edit Billing Address' : 'Add Billing Address'"
      size="lg"
      @close="handleCloseAddressModal('billing'); showAddBillingModal = false"
    >
      <div
        v-if="isLoadingAddress"
        class="v2-flex v2-justify-center v2-p-8"
      >
        <v2-spinner label="Loading address..." />
      </div>
      <v2-address-form
        v-else
        :address="billingFormAddress"
        :countries="countryList"
        :show-cancel="true"
        @save="onSaveBillingAddress"
        @cancel="showAddBillingModal = false"
      />
    </v2-modal>

    <!-- ─── Add Shipping Address Modal ─────────────────────────────────── -->
    <v2-modal
      :show="showAddShippingModal"
      :title="shippingFormAddress && shippingFormAddress._id ? 'Edit Shipping Address' : 'Add Shipping Address'"
      size="lg"
      @close="handleCloseShippingFormModal"
    >
      <div
        v-if="isLoadingAddress"
        class="v2-flex v2-justify-center v2-p-8"
      >
        <v2-spinner label="Loading address..." />
      </div>
      <v2-address-form
        v-else
        :address="shippingFormAddress"
        :countries="countryList"
        :show-cancel="true"
        @save="onSaveShippingAddress"
        @cancel="handleCloseShippingFormModal"
      />
    </v2-modal>

    <!-- ─── Cart Modals (import, clear cart) ───────────────────────────── -->
    <v2-cart-modals
      ref="cartModals"
      import-cart-modal-id="import_cart_modal"
      :clear-cart="clearCart"
      :on-cart-import-success="onCartImportSuccess"
      :place-order="placeOrder"
      :cart-products="$refs.cartProducts"
      :on-click-export-cart="onClickExportCart"
      @on-close-import-cart-modal="trackImportCartEvents('close')"
    />

    <!-- ─── Export Modal ───────────────────────────────────────────────── -->
    <v2-cart-export-modal
      ref="exportModal"
      @on-cart-export-success="onSuccessCartExport"
    />
  </div>
</template>

<script>
import { getCurrentInstance, ref, computed } from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import { debounce } from 'lodash'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import analytics from '@/@core/utils/analytics'
import { formatObject } from '@core/utils/utils'
import constants from '@/constants'
import store from '@/store'
import V2Button from '@/components/v2/V2Button.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import { useCartTotal } from '@/composables/useCartTotal'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import NotificationMixin from '@/views/mixins/NotificationMixin.vue'
import V2AddressDetailOps from '@/views/v2/operations/V2AddressDetail.vue'
import V2AddressForm from '@/views/v2/operations/V2AddressForm.vue'
import { useAddresses } from '@/composables/useAddresses'
import OrderValidationMessage from '@/views/apps/checkout/OrderValidationMessage.vue'
import cartTermsContent from '@/views/apps/checkout/cart-terms-content'
import {
  getTrackNameForAddressModal,
  getTrackNameForImportCartEvents,
} from '@/views/apps/checkout/trackHelpers/cartTotalTrackHelpers'
import V2BuyingSummaryModal from './V2BuyingSummaryModal.vue'
import V2CartProducts from './V2CartProducts.vue'
import V2CartModals from './V2CartModals.vue'
import V2CartExportModal from './V2CartExportModal.vue'

const { ADDRESSES_TYPE } = c

export default {
  name: 'V2RetailerSingleCart',
  components: {
    V2Button,
    V2Card,
    V2Modal,
    V2CartProducts,
    V2CartModals,
    V2CartExportModal,
    V2AddressDetailOps,
    V2AddressForm,
    V2Spinner,
    OrderValidationMessage,
    V2BuyingSummaryModal,
    flatPickr,
  },
  mixins: [UserRoleMixin, NotificationMixin],
  setup(_, { emit }) {
    const showOrderFailModal = ref(false)
    const showTermsModal = ref(false)
    const showBuyingSummaryModal = ref(false)
    const buyingSummaryProducts = ref([])
    const showSelectAddressModal = ref(false)
    const showAddBillingModal = ref(false)
    const showAddShippingModal = ref(false)
    const shippingFormReturnToSelect = ref(false)
    const isLoadingAddress = ref(false)

    const vm = getCurrentInstance()?.proxy

    const cartId = computed(() => store.getters.getCart?._id || '')

    // ─── Sub-modal handlers for useCartTotal composable ──────────────
    const showModalHandlers = {
      'modal-order-fail': () => { showOrderFailModal.value = true },
      'cart-terms': () => { showTermsModal.value = true },
      cart_export_modal: () => { vm?.$refs?.exportModal?.show() },
      'clear-cart': id => { vm?.$refs?.cartModals?.show(id) },
      import_cart_modal: id => { vm?.$refs?.cartModals?.show(id) },
    }

    function onShowSubModal(id) {
      const handler = showModalHandlers[id]
      if (handler) {
        handler(id)
        return
      }
      vm?.$bvModal?.show(id)
    }

    const cartModalsOwnedIds = new Set([
      'clear-cart',
      'import_cart_modal',
      'clear-retailer-cart',
      'modal-upload-cart-conflict',
    ])

    function onHideSubModal(id) {
      if (cartModalsOwnedIds.has(id)) {
        vm?.$refs?.cartModals?.hide(id)
        return
      }
      vm?.$bvModal?.hide(id)
    }

    function getCartProductsRef() {
      return vm?.$refs?.cartProducts || null
    }

    function onOrderSuccess(orderId, orderSeqId) {
      if (vm?.$root?.$orderSuccessPopupContext) {
        vm.$root.$orderSuccessPopupContext.value = {
          orderIds: [{ orderId, orderSeqId }],
        }
      }
      if (vm?.$router) {
        vm.$router.push({
          name: 'v2-order/detail',
          params: { orderId },
        })
      }
    }

    // Non-popup, no retailerId — this is the retailer's own single cart
    const composableProps = {
      isPopup: false,
      cartProducts: [],
      retailerId: null,
      brandNotes: {},
      getCartProductsByBrand: () => {},
      modalId: '',
    }

    const cartTotal = useCartTotal(composableProps, {
      onShowSubModal,
      onHideSubModal,
      getCartProductsRef,
      emit,
      onOrderSuccess,
    })

    // ─── Address composable (replaces AddressMixin) ─────────────────
    const addressComposable = useAddresses()
    const billingFormAddress = ref(addressComposable.newAddress(ADDRESSES_TYPE.BILLING, true))
    const shippingFormAddress = ref(addressComposable.newAddress(ADDRESSES_TYPE.SHIPPING, true))

    return {
      showOrderFailModal,
      showTermsModal,
      showBuyingSummaryModal,
      buyingSummaryProducts,
      showSelectAddressModal,
      showAddBillingModal,
      showAddShippingModal,
      shippingFormReturnToSelect,
      cartId,
      cartTermsContent,
      ADDRESSES_TYPE,
      addressComposable,
      billingFormAddress,
      shippingFormAddress,
      isLoadingAddress,
      ...cartTotal,
    }
  },
  computed: {
    countryList() {
      return this.countryData?.COUNTRIES || []
    },
    displayShippingAddress() {
      return this.pickAddressForType(ADDRESSES_TYPE.SHIPPING) || null
    },
    displayBillingAddress() {
      return this.pickAddressForType(ADDRESSES_TYPE.BILLING) || null
    },
    buyingSummaryCartList() {
      const cart = this.$store.getters.getCart || {}
      return [{
        _id: cart._id || '',
        storeName: cart.storeName || cart.entityName || '',
        products: this.buyingSummaryProducts,
      }]
    },
    buyingSummaryCartValuesList() {
      return [{
        totalUnits: this.totalUnits || 0,
        totalValues: this.totalMRP || 0,
        totalRRP: this.totalMRP || 0,
        currency: this.currency || '',
      }]
    },
    buyingSummaryTotalOrderValue() {
      return {
        totalCartsUnits: this.totalUnits || 0,
        totalCartsValue: this.totalMRP || 0,
        totalCartsRRP: this.totalMRP || 0,
        currency: this.currency || '',
      }
    },
  },
  mounted() {
    this.initialize()
    this.getUserProfile(false)
  },
  methods: {
    // ─── Place order ──────────────────────────────────────────────────
    handlePlaceOrder() {
      if (!this.acceptTerms) {
        this.isTermsUnChecked = true
        return
      }
      this.$refs.cartProducts?.activateErrors()
      this.placeOrder(false)
    },

    // ─── Import modal ─────────────────────────────────────────────────
    openImportModal() {
      this.trackImportCartEvents('button-click')
      this.$refs.cartModals?.show('import_cart_modal')
    },

    // ─── Delivery window ──────────────────────────────────────────────
    handleDeliveryWindowEdit() {
      this.isEditDeliveryWindow = true
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_DELIVERY_WINDOW_ICON,
        {
          cartId: this.$store.getters.getCart?._id ?? '',
          retailerId: this.$store.getters.getCart?.userId,
        },
      )
      this.$nextTick(() => {
        if (this.$refs.deliveryWindowDate?.$el?._flatpickr) {
          this.$refs.deliveryWindowDate.$el._flatpickr.toggle()
        }
      })
    },

    // ─── Buying summary ──────────────────────────────────────────────
    showBuyingSummary() {
      // Capture products snapshot when modal opens ($refs is not reactive)
      const cartProducts = this.$refs.cartProducts
      this.buyingSummaryProducts = cartProducts?.originalProducts || cartProducts?.products || []
      this.showBuyingSummaryModal = true
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_BUYING_SUMMARY,
        {
          cartId: this.$store.getters.getCart?._id ?? '',
        },
      )
    },
    closeBuyingSummary() {
      this.showBuyingSummaryModal = false
    },

    // ─── Back to collection ──────────────────────────────────────────
    gotoCollection(navigate, event) {
      const products = this.$refs.cartProducts?.products || []
      if (products.length) {
        analytics.track(
          constants.TRACKS.ACTIONS.BACK_TO_COLLECTION,
          formatObject(products[0]),
        )
      }
      navigate(event)
    },

    // ─── Address management ──────────────────────────────────────────
    async getUserProfile(isAddressDetail = false) {
      try {
        const { FETCH_PROFILE } = await import('@/store/modules/auth.module')
        const profile = await store.dispatch(FETCH_PROFILE)
        const { getProperProfileData } = await import('@/common-utils')
        this.profileData = getProperProfileData(profile)
        const addresses = profile?.addresses || []
        this.addressComposable.initAddresses(addresses, false)

        if (isAddressDetail) {
          this.showSelectAddressModal = true
          analytics.track(constants.TRACKS.ACTIONS.RETAILER_CHANGES_ADDRESS_IN_CART)
        }
      } catch (err) {
        // error toast handled by axios interceptor
      }
    },

    async handleClickAddressBtn(type) {
      const addressConfig = {
        billing: {
          modalKey: 'showAddBillingModal',
          formKey: 'billingFormAddress',
          addressType: ADDRESSES_TYPE.BILLING,
          getExisting: () => this.addressComposable.billingAddress.value?.[0],
        },
        shipping: {
          modalKey: 'showAddShippingModal',
          formKey: 'shippingFormAddress',
          addressType: ADDRESSES_TYPE.SHIPPING,
          getExisting: () => this.addressComposable.shippingAddress.value?.find(a => a.isPrimary),
        },
      }

      const config = addressConfig[type]
      const modalName = type === 'billing' ? 'modal-add-billing' : 'modal-add-shipping'
      analytics.track(getTrackNameForAddressModal(modalName, 'click'), {
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId,
      })

      this.isLoadingAddress = true
      this[config.modalKey] = true
      await this.getUserProfile(false)
      const existing = config.getExisting()
      this[config.formKey] = existing
        ? JSON.parse(JSON.stringify(existing))
        : this.addressComposable.newAddress(config.addressType, true)
      this.isLoadingAddress = false
    },

    handleCloseAddressModal(type) {
      const modalName = type === 'billing' ? 'modal-add-billing' : 'modal-add-shipping'
      const trackName = getTrackNameForAddressModal(modalName, 'close')
      const addressData = type === 'billing'
        ? this.addressComposable.billingAddress.value
        : this.addressComposable.shippingAddress.value

      analytics.track(trackName, {
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId,
        addressData,
      })
    },

    handleAddUpdateAddress(address) {
      const trackName = address.addressType === ADDRESSES_TYPE.BILLING
        ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ADD_BILLING_ADDRESS_SAVE_BUTTON
        : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ADD_SHIPPING_ADDRESS_SAVE_BUTTON

      this.trackAddressEvents(trackName, address)
      this.addressComposable.addOrUpdateAddress(address)
    },

    onSelectShippingAddress(addr) {
      this.addressComposable.setAsPrimaryAddress(addr._id).then(() => {
        this.showSelectAddressModal = false
        this.getUserProfile(false)
      })
    },

    editShippingAddress(addr) {
      this.shippingFormAddress = JSON.parse(JSON.stringify(addr))
      this.shippingFormReturnToSelect = true
      this.showSelectAddressModal = false
      this.showAddShippingModal = true
    },

    addNewShippingAddress() {
      this.shippingFormAddress = this.addressComposable.newAddress(ADDRESSES_TYPE.SHIPPING, false)
      this.shippingFormReturnToSelect = true
      this.showSelectAddressModal = false
      this.showAddShippingModal = true
    },

    handleCloseShippingFormModal() {
      this.handleCloseAddressModal('shipping')
      this.showAddShippingModal = false
      if (this.shippingFormReturnToSelect) {
        this.shippingFormReturnToSelect = false
        this.showSelectAddressModal = true
      }
    },

    pickAddressForType(type) {
      const composableList = type === ADDRESSES_TYPE.BILLING
        ? this.addressComposable?.billingAddress?.value
        : this.addressComposable?.shippingAddress?.value
      const fromComposable = (composableList || []).find(a => a?.isPrimary && a?.addressLine1)
        || (composableList || []).find(a => a?.addressLine1)
      if (fromComposable) return fromComposable

      const ofType = (this.addresses || []).filter(a => a?.addressType === type)
      const fromAll = ofType.find(a => a?.isPrimary && a?.addressLine1)
        || ofType.find(a => a?.addressLine1)
      if (fromAll) return fromAll

      const legacy = type === ADDRESSES_TYPE.BILLING
        ? this.profileData?.billingAddress
        : this.profileData?.shippingAddress
      return legacy?.addressLine1 ? legacy : null
    },

    onSaveBillingAddress(address, done) {
      address.addressType = ADDRESSES_TYPE.BILLING
      const trackName = constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ADD_BILLING_ADDRESS_SAVE_BUTTON
      this.trackAddressEvents(trackName, address)
      this.addressComposable.addOrUpdateAddress(address).then(() => {
        this.trackAddressSaveSuccess(address)
        this.showAddBillingModal = false
        this.billingFormAddress = this.addressComposable.newAddress(ADDRESSES_TYPE.BILLING, true)
        this.getUserProfile(false)
      }).catch(err => {
        this.trackAddressSaveFailed(address, err)
      }).finally(() => { if (done) done() })
    },

    onSaveShippingAddress(address, done) {
      address.addressType = ADDRESSES_TYPE.SHIPPING
      const trackName = constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ADD_SHIPPING_ADDRESS_SAVE_BUTTON
      this.trackAddressEvents(trackName, address)
      const returnToSelect = this.shippingFormReturnToSelect
      this.addressComposable.addOrUpdateAddress(address).then(() => {
        this.trackAddressSaveSuccess(address)
        this.showAddShippingModal = false
        this.shippingFormAddress = this.addressComposable.newAddress(ADDRESSES_TYPE.SHIPPING, true)
        this.shippingFormReturnToSelect = false
        return this.getUserProfile(false).then(() => {
          if (returnToSelect) {
            this.showSelectAddressModal = true
          }
        })
      }).catch(err => {
        this.trackAddressSaveFailed(address, err)
      }).finally(() => { if (done) done() })
    },

    trackAddressEvents(trackName, address, error) {
      analytics.track(trackName, {
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId || '',
        addressData: address,
        error: error ? error?.message || 'Something went wrong' : undefined,
      })
    },

    trackAddressSaveSuccess(address) {
      const trackName = address.addressType === ADDRESSES_TYPE.BILLING
        ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_BILLING_ADDRESS_SUCCESS
        : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_SHIPPING_ADDRESS_SUCCESS

      this.trackAddressEvents(trackName, address)
    },

    trackAddressSaveFailed(address, error) {
      const trackName = address.addressType === ADDRESSES_TYPE.BILLING
        ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_BILLING_ADDRESS_FAILED
        : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_SHIPPING_ADDRESS_FAILED

      this.trackAddressEvents(trackName, address, error)
    },

    debouncedTrackAddressInputs: debounce(({
      addressType, addressData, cartId: cId, retailerId,
    }) => {
      const trackName = addressType === 'billing'
        ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_ENTERS_DATA_IN_ADD_BILLING_ADDRESS_FORM
        : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_ENTERS_DATA_IN_ADD_SHIPPING_ADDRESS_FORM

      analytics.track(trackName, {
        cartId: cId,
        retailerId,
        addressData,
      })
    }, 500),

    trackAddressInputChange(val, type) {
      this.debouncedTrackAddressInputs({
        addressType: type,
        addressData: val,
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId || '',
      })
    },

    // ─── Import/export analytics ─────────────────────────────────────
    trackImportCartFileEvents(eventName = 'file-select', fileName, error) {
      const trackName = getTrackNameForImportCartEvents(eventName, true)
      analytics.track(trackName, {
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId || '',
        products: this.$refs.cartProducts?.products || [],
        fileName,
        error: error ? error?.message ?? 'Something went wrong' : undefined,
      })
    },

    // ─── Export analytics ────────────────────────────────────────────
    trackExportCartEvent(eventName, exportType = null, error = null) {
      const trackNameMap = {
        close: constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLOSES_EXPORT_CART_MODAL,
        download: constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ON_EXPORT_MODAL_DOWNLOAD_BUTTON,
        failed: constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_EXPORT_FILE_FAILED,
      }
      const trackName = trackNameMap[eventName]
      analytics.track(trackName, {
        cartId: this.$store.getters.getCart?._id ?? '',
        retailerId: this.$store.getters.getCart?.userId || '',
        products: this.$refs.cartProducts?.products || [],
        exportType,
        error: error?.message ?? 'Something went wrong',
      })
    },

    onSuccessCartExport(exportType) {
      this.onCartExportSuccess()
      this.notifyDownloadsNavMenu()
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_EXPORT_FILE_SUCCESS,
        {
          cartId: this.$store.getters.getCart?._id ?? '',
          retailerId: this.$store.getters.getCart?.userId || '',
          products: this.$refs.cartProducts?.products || [],
          exportType,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-flatpicker.scss';

.v2-retailer-single-cart {
  /* ─── Action bar ────────────────────────────────────────────────── */
  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__actions-left,
  &__actions-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* ─── Two-column grid ───────────────────────────────────────────── */
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    &__grid {
      grid-template-columns: 1fr;
    }
  }

  &__left,
  &__right {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* ─── Section headers ───────────────────────────────────────────── */
  &__section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  &__section-title {
    font-family: 'Host Grotesk', 'Inter', sans-serif;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--v2-foreground, #1A0A3E);
    margin: 0;
  }

  /* ─── Edit button ───────────────────────────────────────────────── */
  &__edit-btn {
    background: none;
    border: none;
    color: var(--v2-muted-foreground, #5E4B7B);
    cursor: pointer;
    padding: 2px;
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--v2-primary, #747DEE);
    }
  }

  /* ─── Notes ─────────────────────────────────────────────────────── */
  /* Glass textarea — matches V2Input recipe. */
  &__notes-input {
    width: 100%;
    min-height: 80px;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    color: var(--v2-foreground, #1A0A3E);
    background: var(--v2-glass-surface-2);
    resize: vertical;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary, #747DEE);
    }
  }

  &__notes-text {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground, #5E4B7B);
  }

  /* ─── Delivery Window ───────────────────────────────────────────── */
  &__field {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: var(--v2-foreground, #1A0A3E);
  }

  &__delivery-edit {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;

    /* Glass override for the flatpickr month picker — Bootstrap's
     * .form-control class otherwise renders a flat white field with a
     * faint gray border. We give it the V2Input glass recipe with a
     * stronger glass-stroke for visibility. */
    .form-control {
      background: var(--v2-glass-surface-2);
      border: 1px solid var(--v2-glass-stroke-strong);
      border-radius: var(--v2-radius-md);
      color: var(--v2-foreground);
      transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

      &:hover:not(:focus) {
        border-color: var(--v2-primary);
      }

      &:focus {
        border-color: var(--v2-primary);
        box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
      }
    }
  }

  /* ─── Address section ───────────────────────────────────────────── */
  &__address-section {
    margin-top: 1.25rem;
  }

  &__address-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: var(--v2-foreground, #1A0A3E);
  }

  &__address-note {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground, #5E4B7B);
    margin-bottom: 0.75rem;
  }

  &__address-select-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--v2-border, #E5E3F1);
    border-radius: 8px;
    margin-bottom: 0.5rem;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-primary, #747DEE);
    }

    input[type='radio'] {
      margin-top: 0.25rem;
      accent-color: var(--v2-primary, #747DEE);
    }
  }

  &__address-select-body {
    flex: 1;
    min-width: 0;
  }

  &__address-edit-btn {
    flex-shrink: 0;
    align-self: center;
  }

  &__address-add-row {
    margin-top: 0.75rem;
  }

  /* ─── Address form actions inside modals: centered ─────────────── */
  .v2-address-form__actions {
    justify-content: center;
  }

  /* ─── Order totals ──────────────────────────────────────────────── */
  &__totals {
    margin-bottom: 1rem;
  }

  &__total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    color: var(--v2-foreground, #1A0A3E);
    padding: 0.375rem 0;

    &--bold {
      font-weight: 700;
      font-size: 1rem;
    }
  }

  &__divider {
    height: 1px;
    background-color: var(--v2-border, #E5E3F1);
    margin: 0.5rem 0;
  }

  /* ─── Credits ───────────────────────────────────────────────────── */
  &__credits {
    margin-top: 0.5rem;
  }

  &__credits-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground, #1A0A3E);
    margin-bottom: 0.25rem;
  }

  &__credits-action {
    color: var(--v2-primary, #747DEE);
    cursor: pointer;
    margin-left: 0.5rem;
    font-weight: 400;
  }

  &__credits-input-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.375rem;
  }

  &__credits-input {
    width: 120px;
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--v2-border, #E5E3F1);
    border-radius: 8px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--v2-primary, #747DEE);
    }
  }

  /* ─── Terms ─────────────────────────────────────────────────────── */
  &__terms {
    margin-top: auto;
    padding-top: 0.75rem;
  }

  &__terms-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
  }

  &__terms-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    color: var(--v2-foreground, #1A0A3E);
    margin: 0;

    /* Custom glass checkbox — mirrors V2Checkbox so the agree-to-terms
     * box matches the rest of the v2 form controls. appearance:none
     * removes the OS default; we draw the box ourselves. */
    input[type='checkbox'] {
      appearance: none;
      -webkit-appearance: none;
      width: 1rem;
      height: 1rem;
      margin: 0;
      border: 1.5px solid var(--v2-glass-stroke-strong);
      border-radius: var(--v2-radius-sm);
      background: var(--v2-glass-surface-2);
      cursor: pointer;
      flex-shrink: 0;
      position: relative;
      transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

      &:hover {
        border-color: var(--v2-primary);
      }

      &:checked {
        background: var(--v2-primary);
        border-color: var(--v2-primary);
        backdrop-filter: none;

        /* Tick mark */
        &::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 5px;
          height: 9px;
          border: solid var(--v2-primary-foreground);
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
      }
    }
  }

  &__terms-link {
    color: var(--v2-muted-foreground, #5E4B7B);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--v2-primary, #747DEE);
    }
  }

  &__terms-error {
    font-size: 0.75rem;
    color: var(--v2-destructive, #E5484D);
    margin-top: 0.25rem;
  }

  /* ─── Place Order ───────────────────────────────────────────────── */
  &__place-order {
    margin-top: 0.75rem;
  }
}

/* ─── Terms modal content (shared styling) ────────────────────────── */
.v2-cart-terms {
  &__heading {
    font-family: 'Host Grotesk', 'Inter', sans-serif;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--v2-foreground, #1A0A3E);
    margin-bottom: 1rem;
  }

  &__section {
    margin-bottom: 1.25rem;
  }

  &__section-title {
    font-weight: 600;
    font-size: 0.9375rem;
    color: var(--v2-foreground, #1A0A3E);
    margin-bottom: 0.5rem;
  }

  &__point {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground, #5E4B7B);
    line-height: 1.6;
    margin-bottom: 0.25rem;
  }
}
</style>
